import React, { useState, useEffect, useRef } from 'react';
import { useIdle, useVideo, useToggle, useFullscreen } from 'react-use';
import Modal from 'react-aria-modal';
import Slider from 'react-input-slider';
import cx from 'classnames';
import styles from './player.module.scss';
import Cursor from '../global/cursor/Cursor';
import Media from '../../models/media';
import Button from '../../models/button';

import Close from '../../public/svg/close.svg';
import Rewind from '../../public/svg/rewind.svg';
import PlayRounded from '../../public/svg/playRounded.svg';
import PauseRounded from '../../public/svg/pauseRounded.svg';
import FastForward from '../../public/svg/fastForward.svg';
import Play from '../../public/svg/play.svg';
import Pause from '../../public/svg/pause.svg';
import SoundOn from '../../public/svg/soundOn.svg';
import SoundOff from '../../public/svg/soundOff.svg';
import FullscreenOn from '../../public/svg/fullscreenOn.svg';
import FullscreenOff from '../../public/svg/fullscreenOff.svg';

type PlayerType = {
  inlineMedia?: Media;
  inlineClass?: string;
  fullscreenMedia?: Media;
  cursor?: Button;
  aspectRatio?: string;
  thumbnail?: number;
};

const Player = ({
  inlineMedia,
  inlineClass,
  fullscreenMedia,
  cursor,
  aspectRatio,
  thumbnail
}: PlayerType) => {
  const [bgVideo, bgState, bgControls] = useVideo(
    <video
      src={`${inlineMedia?.src}#t=${thumbnail || 0.001}`}
      className={cx(styles.player__video, inlineClass)}
      loop
      muted
    />
  );

  const [video, state, controls, videoRef] = useVideo(
    <video
      src={fullscreenMedia?.src}
      className={styles.player__video}
      loop
      muted
    />
  );

  const isIdle = useIdle(2000);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [scrubValue, setScrubValue] = useState(
    (100 * state.time) / state.duration
  );

  const ref = useRef(null);
  const [appNode, setAppNode] = useState<Node | null>(null);
  const [show, toggle] = useToggle(false);
  const isFullscreen = useFullscreen(ref, show, {
    onClose: () => toggle(false)
  });

  useEffect(() => {
    setScrubValue((100 * state.time) / state.duration);
  }, [state.time]);

  useEffect(() => {
    setAppNode(document.querySelector('.main'));
  }, []);

  function openModal() {
    setIsOpen(true);
  }

  function onEnter() {
    document.documentElement.style.overflow = 'hidden';
    if (videoRef.current) {
      controls.play();
      controls.unmute();
    }
  }

  function closeModal() {
    if (show) {
      toggle(false);
      setTimeout(() => {
        setIsOpen(false);
        bgControls.pause();
        document.documentElement.style.removeProperty('overflow');
      }, 1000);
      return;
    }
    setIsOpen(false);
    bgControls.pause();
    document.documentElement.style.removeProperty('overflow');
  }

  function getPlayback(current: number, total: number) {
    function secondsToTime(e: number) {
      // eslint-disable-next-line no-mixed-operators
      const m = Math.floor((e % 3600) / 60)
        .toString()
        .padStart(2, '0');
      const s = Math.floor(e % 60)
        .toString()
        .padStart(2, '0');

      return `${m}:${s}`;
    }

    return `${secondsToTime(current)} / ${secondsToTime(total)}`;
  }

  return (
    <>
      <button
        type="button"
        className={cx(styles.player__modalButton, styles.player__button)}
        onClick={openModal}
        onMouseEnter={bgControls.play}
        onMouseLeave={bgControls.pause}
        name="player control"
      >
        {aspectRatio && (
          <div
            className={styles.player__aspect}
            style={{ paddingTop: `calc((${aspectRatio}) * 100%)` }}
          />
        )}
        {cursor ? <Cursor data={cursor}>{bgVideo}</Cursor> : bgVideo}
        <div
          className={styles.player__playIcon}
          style={{ color: 'black', opacity: bgState.paused ? 1 : 0 }}
        >
          <Play />
        </div>
      </button>
      {appNode && (
        <Modal
          titleText="Fullscreen Video"
          mounted={modalIsOpen}
          onEnter={onEnter}
          onExit={closeModal}
          applicationNode={appNode}
          underlayColor="rgba(0,0,0,0.95)"
          underlayStyle={{ overflow: 'hidden' }}
          verticallyCenter
          scrollDisabled={false}
          initialFocus="#centerPause"
        >
          <div ref={ref} className={styles.player__wrapper}>
            {video}
            <div
              className={styles.player__controls}
              style={{ opacity: isIdle ? '0' : '1' }}
            >
              <div className={styles.player__controlsTop}>
                <button
                  className={styles.player__button}
                  type="button"
                  onClick={closeModal}
                  name="player close modal"
                >
                  <Close />
                </button>
              </div>

              <div className={styles.player__controlsCenter}>
                <button
                  className={styles.player__button}
                  type="button"
                  onClick={() => controls.seek(state.time - 10)}
                  name="player control rewind"
                >
                  <Rewind />
                </button>
                <button
                  id="centerPause"
                  className={cx(
                    styles.player__button,
                    styles.player__centerPause
                  )}
                  type="button"
                  onClick={state.paused ? controls.play : controls.pause}
                  name="player control play-pause"
                >
                  {state.paused ? <PlayRounded /> : <PauseRounded />}
                </button>
                <button
                  className={styles.player__button}
                  type="button"
                  onClick={() => controls.seek(state.time + 10)}
                  name="player control fastforward"
                >
                  <FastForward />
                </button>
              </div>
              <div className={styles.player__controlsBottom}>
                <Slider
                  styles={{
                    track: {
                      width: 'auto',
                      gridColumn: '1/-1',
                      backgroundColor: '#FFFFFF80',
                      cursor: 'pointer'
                    },
                    active: {
                      background: 'white'
                    }
                  }}
                  x={scrubValue}
                  onChange={({ x }) => {
                    controls.seek((x * state.duration) / 100);
                  }}
                />
                <div className={styles.player__controlsLeft}>
                  <button
                    type="button"
                    className={cx(
                      styles.player__leftIcon,
                      styles.player__button
                    )}
                    onClick={state.paused ? controls.play : controls.pause}
                    name="player control play-pause"
                  >
                    {state.paused ? <Play /> : <Pause />}
                  </button>
                  <button
                    type="button"
                    className={cx(
                      styles.player__leftIcon,
                      styles.player__button
                    )}
                    onClick={state.muted ? controls.unmute : controls.mute}
                    name="player control mute-unmute"
                  >
                    {state.muted ? <SoundOff /> : <SoundOn />}
                  </button>
                  <span className={styles.player__timer}>
                    {getPlayback(state.time, state.duration)}
                  </span>
                </div>
                <div className={styles.player__controlsRight}>
                  <button
                    type="button"
                    className={cx(
                      styles.player__rightIcon,
                      styles.player__button
                    )}
                    onClick={() => {
                      toggle();
                    }}
                    name="player control screen size"
                  >
                    {isFullscreen ? <FullscreenOn /> : <FullscreenOff />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Player;
